// @givmo/app-charity — api-client wiring (Combined Plan 03 §4 consumer).
//
// We CONSUME @givmo/api-client (the typed fetch client + the ported Dio interceptor stack: Bearer ID
// token, X-Firebase-AppCheck, X-Request-Id correlation, {data,meta,links} envelope unwrap,
// page[limit]/page[offset]); we do NOT re-implement it. This module only supplies the charity-app
// CONFIGURATION:
//   - The SPA configures the SHARED singleton ONCE at boot; tokens come from the LIVE Firebase
//     session — the Bearer ID token from @givmo/auth's `auth.currentUser`, App Check from the
//     reCAPTCHA Enterprise provider — both resolved lazily per request.
//
// Charity-app config: `appPrefix: 'ch'` (the charity X-Request-Id prefix; admin uses 'wb',
// super-admin 'sa', donate 'dw'), base URL from env (api-dev.givmo.io dev / api.givmo.io prod —
// env-injected), Bearer token provider, App Check provider, and the console network logger gated on
// `!isProduction`.
//
// Note: the charity-specific `/charity-admin/*` surface (CPA-BE1) IS live and the charity read/write
// features (memberships, §321 grants, claim, invites, payment-profile) call it through this SAME
// configured client, alongside the shared auth endpoints (email verification / OTP).

import {
  configureGivmoClient,
  type ConfiguredGivmoClient,
  type FirebaseAuthProvider,
  type FirebaseAppCheckProvider,
} from '@givmo/api-client';
import { getGivmoAppCheckToken, type SuiteSessionTransport } from '@givmo/auth';
import type { Auth } from 'firebase/auth';
import type { AppCheck } from 'firebase/app-check';
import type { CharityEnv } from './env';
import { isProduction } from './env';
import { CHARITY_REQUEST_ID_PREFIX } from './constants';

/**
 * Bearer ID-token provider backed by the live Firebase Auth session. Returns `null` when no user is
 * signed in (the api-client middleware then omits the `Authorization` header — unauthenticated calls
 * such as the pre-login surface still work). `forceRefresh` bypasses the cached token (e.g. after a
 * 401) per the {@link FirebaseAuthProvider} contract.
 */
export function firebaseAuthProvider(auth: Auth): FirebaseAuthProvider {
  return {
    async getIdToken(forceRefresh = false): Promise<string | null> {
      const user = auth.currentUser;
      if (user === null) return null;
      return user.getIdToken(forceRefresh);
    },
  };
}

/**
 * App Check token provider backed by the reCAPTCHA Enterprise AppCheck instance. FAIL-CLOSED via
 * @givmo/auth's `getGivmoAppCheckToken` (it throws rather than minting an empty token). When App
 * Check is not configured (`appCheck` is `null` — local/dev), returns a provider that yields `null`
 * so the middleware omits the header (the backend bypasses App Check in local/dev).
 */
export function firebaseAppCheckProvider(appCheck: AppCheck | null): FirebaseAppCheckProvider {
  if (appCheck === null) {
    return { getToken: async () => null };
  }
  return {
    async getToken(): Promise<string | null> {
      const { token } = await getGivmoAppCheckToken(appCheck);
      return token;
    },
  };
}

export interface ConfigureCharityClientOptions {
  env: CharityEnv;
  auth: Auth;
  appCheck: AppCheck | null;
}

/**
 * Configure the shared @givmo/api-client singleton for the charity app. Call ONCE at boot; every
 * generated `sdk.*` call and TanStack Query option factory then inherits the base URL + interceptor
 * stack. Returns the configured client handle.
 */
/**
 * The resolver for the assistant's SSE stream headers (EMC-WEB10, the email designer). The stream cannot
 * go through the shared fetch client (`@givmo/ai` reads a `ReadableStream` and reconnects on its own), so
 * it takes its two load-bearing headers from here, minted per (re)connect from the same providers the
 * client's interceptors use. Set once at boot by {@link configureCharityClient}.
 */
let resolveStreamHeaders: (() => Promise<Record<string, string>>) | null = null;

/** Auth headers for the assistant stream: empty before boot and for a signed-out session. */
export function assistantStreamHeaders(): Promise<Record<string, string>> {
  return resolveStreamHeaders?.() ?? Promise.resolve({});
}

export function configureCharityClient(
  options: ConfigureCharityClientOptions,
): ConfiguredGivmoClient {
  const { env, auth, appCheck } = options;
  const authProvider = firebaseAuthProvider(auth);
  const appCheckProvider = firebaseAppCheckProvider(appCheck);
  resolveStreamHeaders = async () => {
    const headers: Record<string, string> = {};
    const idToken = await authProvider.getIdToken();
    if (idToken !== null) headers.Authorization = `Bearer ${idToken}`;
    // Fail-soft, as the company console's: a stream that never opens because attestation hiccuped is
    // worse mid-conversation than one the backend refuses with a reason.
    try {
      const appCheckToken = await appCheckProvider.getToken();
      if (appCheckToken !== null) headers['X-Firebase-AppCheck'] = appCheckToken;
    } catch {
      // The backend refuses an unattested stream where App Check is enforced.
    }
    return headers;
  };
  return configureGivmoClient({
    appPrefix: CHARITY_REQUEST_ID_PREFIX,
    baseUrl: env.apiBaseUrl,
    auth: authProvider,
    appCheck: appCheckProvider,
    debugLogEnabled: !isProduction(env),
  });
}

/**
 * The transport `@givmo/auth`'s suite-session primitives need to reach the API host (AS-SUITE4).
 *
 * Deliberately built from the app's EXISTING App Check provider rather than a second token path, so
 * the fail-closed posture is the same one every other request here has: a configured App Check that
 * cannot mint THROWS, and the primitive abandons the call rather than sending it unattested; an
 * unconfigured one (local/dev, no site key) resolves `null` and the header is omitted, exactly as
 * the api-client middleware omits it.
 *
 * `baseUrl` is the same `env.apiBaseUrl` the client is configured with — the suite cookie is
 * host-only on the API host, so any other value silently yields "no suite session".
 */
export function suiteSessionTransport(
  env: CharityEnv,
  appCheck: AppCheck | null,
): SuiteSessionTransport {
  const provider = firebaseAppCheckProvider(appCheck);
  return { baseUrl: env.apiBaseUrl, appCheckToken: () => provider.getToken() };
}
